import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {RolesAccount, RoleType} from '@/app/auth/constants/AuthConstants';
import {featureFlags} from '@/config/featureFlags';
import {LINKS} from '@/constants/LinksConstants';
import {isRoleType} from '@/utils/isRoleType';

import {
  AD_IDS_ITEM,
  PROVIDER_AD_LIST,
  SIGN_UP_QUERY_PARAMS,
  USER_COHORT_ITEM,
} from '../models/constants';

interface UseSignUpQueryParamsResult {
  role: RoleType;
  setRole: Dispatch<SetStateAction<RoleType>>;
  invitationCode: string;
  setInvitationCode: Dispatch<SetStateAction<string>>;
  referralCode: string;
  setReferralCode: Dispatch<SetStateAction<string>>;
  signUpCode: string;
  setSignUpCode: Dispatch<SetStateAction<string>>;
  showInputReferralCode: boolean;
  setShowInputReferralCode: Dispatch<SetStateAction<boolean>>;
  enableBankTesting: boolean | undefined;
  queryParamsProcessed: boolean;
}

// Reads and reacts to every /authentication/sign-up query param (role,
// invitation/referral codes, ad provider click ids, cohort, bank testing
// flag) so the ViewModelContext only has to consume the resulting state.
export function useSignUpQueryParams(
  search: string,
): UseSignUpQueryParamsResult {
  const [role, setRole] = useState<RoleType>(RolesAccount.PARENT);
  const [invitationCode, setInvitationCode] = useState<string>('');
  const [referralCode, setReferralCode] = useState<string>('');
  const [signUpCode, setSignUpCode] = useState<string>('');
  const [showInputReferralCode, setShowInputReferralCode] =
    useState<boolean>(false);
  const [enableBankTesting, setEnableBankTesting] = useState<
    boolean | undefined
  >(undefined);
  const [queryParamsProcessed, setQueryParamsProcessed] = useState(false);

  const checkRedirectUserToWaitList = useCallback((cohort: string) => {
    const isSignUpDisabled = featureFlags.isEnabledWaitList;
    if (isSignUpDisabled && !cohort) {
      window.location.href = LINKS.WAIT_LIST;
    }
  }, []);

  const handleCohortQueryParam = useCallback((cohort: string) => {
    localStorage.setItem(USER_COHORT_ITEM, cohort);
  }, []);

  const handleMatchedAdProviders = useCallback(
    (queryParams: URLSearchParams) => {
      const params = Object.fromEntries(queryParams.entries());
      const matchedAdProviders = Object.keys(params).filter(key =>
        PROVIDER_AD_LIST.includes(key),
      );

      if (matchedAdProviders.length > 0) {
        const adData = matchedAdProviders.reduce((acc, provider) => {
          acc[provider] = params[provider];
          return acc;
        }, {});

        localStorage.setItem(AD_IDS_ITEM, JSON.stringify(adData));
      }
    },
    [],
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const queryRole = queryParams.get(SIGN_UP_QUERY_PARAMS.ROLE)?.toUpperCase();
    if (isRoleType(queryRole)) {
      setRole(queryRole);
    }

    const cohort = queryParams.get(USER_COHORT_ITEM);
    if (cohort) {
      handleCohortQueryParam(cohort);
    }
    checkRedirectUserToWaitList(cohort);

    const familyCode = queryParams.get(SIGN_UP_QUERY_PARAMS.INVITATION_CODE);
    if (familyCode) {
      setInvitationCode(familyCode);
    }

    const queryReferralCode = queryParams.get(
      SIGN_UP_QUERY_PARAMS.REFERRAL_CODE,
    );
    if (queryReferralCode) {
      setReferralCode(queryReferralCode);
      setSignUpCode(queryReferralCode);
      setShowInputReferralCode(true);
    }

    if (familyCode && !queryReferralCode) {
      setSignUpCode(familyCode);
      setShowInputReferralCode(true);
    }

    const enableBankTestingParam = queryParams.get(
      SIGN_UP_QUERY_PARAMS.ENABLE_BANK_TESTING,
    );
    if (enableBankTestingParam !== null) {
      setEnableBankTesting(enableBankTestingParam.toLowerCase() === 'true');
    } else {
      setEnableBankTesting(undefined);
    }

    handleMatchedAdProviders(queryParams);
    setQueryParamsProcessed(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, handleMatchedAdProviders]);

  return {
    role,
    setRole,
    invitationCode,
    setInvitationCode,
    referralCode,
    setReferralCode,
    signUpCode,
    setSignUpCode,
    showInputReferralCode,
    setShowInputReferralCode,
    enableBankTesting,
    queryParamsProcessed,
  };
}
